import { computed, action, observable } from 'mobx';
import { ProductBaseController } from './ProductBaseController';
import { emit, events, on } from '@estee/elc-service-bus';
import { SECONDS } from '../constants/TimeoutSeconds';
import { IProductBriefViewControllerData, IProductBriefProps } from '../interfaces/IProductBrief';
import { ComponentTypes } from '../constants/ComponentTypes';
import { QueryFragments } from '~api/QueryFragments';
import { ProductRepository } from '~repositories/ProductRepository';
import { Product } from '~domain/entities/Product';
import { Sku } from '~domain/entities/Sku';
import { IFeatures } from '~constants/Features';

interface IResolveProduct {
    product?: Product;
    productId?: string;
    displayableSkus?: string[];
    features?: IFeatures;
}

export class ProductBriefViewController extends ProductBaseController {
    @observable private isProductQuickViewOpen: boolean = false;
    @observable public isLoaded: boolean = false;
    private queryFragments: QueryFragments;
    private productRepository: ProductRepository;

    constructor(productRepository: ProductRepository, queryFragments: QueryFragments) {
        super();
        this.productRepository = productRepository;
        this.queryFragments = queryFragments;
    }

    @action
    public openProductQuickView = () => {
        this.isProductQuickViewOpen = true;
    };

    @action
    public closeProductQuickView = () => {
        this.isProductQuickViewOpen = false;
    };

    @action
    public onQuickViewButtonClick = async (skuId: string) => {
        this.openProductQuickView();
        emit(events.QUICK_VIEW_TRIGGERED, {
            skuId,
            positionIndex: this.positionIndex
        });
    };

    @computed
    get data(): IProductBriefViewControllerData {
        const {
            product,
            selectedSku,
            largeMedia,
            isProductQuickViewOpen,
            isShortDescriptionEnabled,
            // backwards compatibility for legacy briefs
            productBriefImageCarouselSettings
        } = this;

        return {
            ...this.baseData,
            displayName: product.displayNameDecoded,
            productBadge: product.productBadge,
            selectedSku,
            skuBadge: selectedSku.skuBadge,
            largeMedia,
            alternateImage: this.getCustomSizeAlternateImage(ComponentTypes.PRODUCT_BRIEF),
            product,
            isProductQuickViewOpen,
            isShortDescriptionEnabled,
            productBriefImageCarouselSettings
        };
    }

    @action
    public initialize = async (props: IProductBriefProps) => {
        const { product, productId, displayableSkus, features } = props;

        const resolvedProduct = await this.resolveProduct({
            product,
            productId,
            displayableSkus,
            features
        });
        this.setProduct(resolvedProduct);
        this.positionIndex = props.positionIndex || 0;

        if (resolvedProduct) {
            this.isLoaded = true;
            this.selectBestShade();
        }
    };

    private async resolveProduct(params: IResolveProduct): Promise<Product> {
        const { product, productId, displayableSkus, features } = params;

        if (product) {
            return product;
        }

        this.queryFragments.setFeatures(<IFeatures>features);

        const fetchedProduct = (await this.productRepository.getDebouncedProductData(
            productId as string
        )) as Product;

        if (displayableSkus && displayableSkus.length > 0) {
            const filteredSkus = fetchedProduct.skus.filter((sku: Sku) =>
                displayableSkus.includes(sku.skuId)
            );
            fetchedProduct.setSkus(filteredSkus);
        }

        return fetchedProduct;
    }

    @computed
    private get productBriefImageCarouselSettings() {
        return (
            this.imageCarouselSettings && this.imageCarouselSettings[ComponentTypes.PRODUCT_BRIEF]
        );
    }

    @computed
    private get productBriefShortDescriptionSettings() {
        return this.shortDescriptionSettings &&
            this.shortDescriptionSettings.overrides &&
            this.shortDescriptionSettings.overrides[ComponentTypes.PRODUCT_BRIEF]
            ? {
                  ...this.shortDescriptionSettings,
                  ...this.shortDescriptionSettings.overrides[ComponentTypes.PRODUCT_BRIEF]
              }
            : this.shortDescriptionSettings;
    }

    @computed
    private get isShortDescriptionEnabled() {
        const shortDescriptionSettings = this.productBriefShortDescriptionSettings;

        return shortDescriptionSettings && shortDescriptionSettings.enabled;
    }

    @computed
    get targetUrl(): string {
        const {
            selectedSku,
            product,
            data: { selectedProductUrl }
        } = this;

        return selectedSku.isDefaultSku ? product.productUrl : selectedProductUrl;
    }

    @on(events.CART_UPDATED)
    public cartUpdated = () => {
        this.closeProductQuickView();
    };

    @on(events.FAVORITE_STATUS_UPDATED)
    public favoriteUpdated = () => {
        setTimeout(() => this.closeProductQuickView(), SECONDS.ONE);
    };

    @on(events.CART_FAILURE)
    public cartFailure = () => {
        this.closeProductQuickView();
    };
}
